import React from 'react';
import './NotificationPreferences.css';
import { isPendingConfirmationReplyOnlyEnabled } from '../../../config/featureFlags';
import { defaultTemplates } from '../constants/defaultTemplates';

const PREFERENCES = [
  {
    key: 'bookingConfirmation',
    title: 'Booking confirmations',
    description: 'Send a confirmation when an appointment is approved.'
  },
  {
    key: 'appointmentReminders',
    title: 'Appointment reminders',
    description: 'Send reminder texts before upcoming appointments.'
  },
  {
    key: 'schedulingUpdates',
    title: 'Schedule changes',
    description: 'Notify clients about reschedules or cancellations.'
  },
  {
    key: 'pendingConfirmationRequest',
    title: 'Client confirmation requests',
    description: isPendingConfirmationReplyOnlyEnabled()
      ? 'Send SMS asking clients to reply CONFIRM to confirm attendance.'
      : 'Send SMS when a client should confirm attendance (link or CONFIRM reply; see below).'
  },
  {
    key: 'checkInLinkRequest',
    title: 'Check-in links',
    description: 'Send automated check-in links before appointments (and customize the message in the template below).'
  }
];

const getNotificationDefault = (type) => defaultTemplates.notifications[type] ?? false;

const getNotificationValue = (settings, type) =>
  settings?.notifications?.[type] ?? getNotificationDefault(type);

const NotificationPreferences = ({ settings, onUpdate, isTrial = false }) => {
  const handleToggle = (type) => {
    if (isTrial) {
      return;
    }

    const updatedSettings = {
      ...settings,
      notifications: {
        ...settings.notifications,
        [type]: !getNotificationValue(settings, type)
      },
      ...(type === 'appointmentReminders' && getNotificationValue(settings, type) && {
        reminderEnabled: false,
        reminders: []
      })
    };

    onUpdate(updatedSettings);
  };

  const handleTogglePendingConfirmationReplyOnly = () => {
    if (isTrial) {
      return;
    }
    onUpdate({
      ...settings,
      pendingConfirmationUseReply: !settings.pendingConfirmationUseReply
    });
  };

  return (
    <div className="sms-notification-preferences">
      <div className="sms-notification-preferences-list">
      {PREFERENCES.map((preference) => (
        <React.Fragment key={preference.key}>
          <div
            className={`sms-notification-preference-row ${isTrial ? 'disabled' : ''}`}
          >
            <div className="sms-notification-preference-copy">
              <h3 className="sms-notification-preference-title">{preference.title}</h3>
              <p className="sms-notification-preference-description">
                {preference.description}
                {preference.key === 'appointmentReminders' && isTrial ? ' Disabled on trial accounts.' : ''}
              </p>
            </div>

            <label
              className="sms-notification-preference-checkbox"
              onClick={(event) => event.stopPropagation()}
            >
              <input
                type="checkbox"
                checked={getNotificationValue(settings, preference.key)}
                onChange={() => handleToggle(preference.key)}
                disabled={isTrial}
                aria-label={preference.title}
              />
            </label>
          </div>

          {preference.key === 'pendingConfirmationRequest' &&
            settings.notifications?.pendingConfirmationRequest &&
            !isPendingConfirmationReplyOnlyEnabled() && (
            <div className={`sms-notification-preference-sub ${isTrial ? 'disabled' : ''}`}>
              <label className="sms-notification-preference-sub-inner">
                <input
                  type="checkbox"
                  checked={Boolean(settings.pendingConfirmationUseReply)}
                  onChange={handleTogglePendingConfirmationReplyOnly}
                  disabled={isTrial}
                />
                <span>
                  Ask clients to reply CONFIRM instead of sending a confirmation link.
                </span>
              </label>
            </div>
          )}
        </React.Fragment>
      ))}
      </div>

      {isTrial && (
        <div className="sms-notification-trial-message">
          SMS for appointments is disabled during the trial. You can still send up to 10 test SMS from this page.
        </div>
      )}
    </div>
  );
};

export default NotificationPreferences;